import React, { useMemo } from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts';
import { BarChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { Sec, SecTitle, Box, Mini, MiniItem, Plot, Note } from '../styled';
import LoadingML from '@/components/LoadingML';
import { fmtThousand } from '@/utils/comUtil';

echarts.use([
  GridComponent,
  TooltipComponent,
  LegendComponent,
  CanvasRenderer,
  BarChart,
]);

// 分场次销售：按 demo-开发用.html 视觉
//   标题 + mini（总场次 / 全渠道累计已售 / 剩余可售）+ 堆叠柱（260px）
// HTML：#1565c0 全渠道已售，#90caf9 剩余可售
// 数据：state.performSales.sessions
const SessionSales = ({ data, loading }) => {
  const { sessions = [], totalSold = 0, totalRemain = 0 } = data || {};

  const option = useMemo(() => {
    const labels = sessions.map((s) => s.time);
    const sold = sessions.map((s) => s.sold);
    const remain = sessions.map((s) => s.remain);
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        confine: true,
        formatter: (params) => {
          const idx = params[0].dataIndex;
          const x = sessions[idx];
          const total = x.sold + x.remain;
          const rows = [
            {
              color: '#1565c0',
              name: '全渠道已售',
              value: fmtThousand(x.sold, 0),
            },
            {
              color: '#90caf9',
              name: '剩余可售',
              value: fmtThousand(x.remain, 0),
            },
            { color: '#8c939e', name: '合计', value: fmtThousand(total, 0) },
          ];
          const rowHtml = rows
            .map(
              (r) =>
                '<div style="display:flex;align-items:center;justify-content:space-between;line-height:18px;">' +
                `<span><span style="display:inline-block;margin-right:6px;border-radius:50%;width:8px;height:8px;background-color:${r.color};vertical-align:middle;"></span>${r.name}</span>` +
                `<span style="font-weight:bold;text-align:right;padding-left:24px;">${r.value}</span>` +
                '</div>',
            )
            .join('');
          return (
            '<div style="font-size:12px;color:#4e5969;">' +
            `<div style="font-weight:600;line-height:18px;margin-bottom:2px;">${x.time}</div>` +
            rowHtml +
            '</div>'
          );
        },
      },
      legend: {
        show: true,
        top: 8,
        left: 12,
        padding: 0,
        icon: 'roundRect',
        itemWidth: 10,
        itemHeight: 10,
        itemGap: 16,
        textStyle: { color: '#8c939e', fontSize: 12 },
        data: ['全渠道已售', '剩余可售'],
      },
      grid: { top: 36, left: 12, right: 8, bottom: 28, containLabel: true },
      xAxis: {
        type: 'category',
        data: labels,
        axisLabel: { color: '#8c939e', fontSize: 10 },
        axisLine: { lineStyle: { color: '#e2e7ec' } },
      },
      yAxis: {
        type: 'value',
        axisLabel: { color: '#8c939e', fontSize: 11 },
        splitLine: { lineStyle: { color: '#f0f1f5' } },
        axisTick: { show: false },
      },
      series: [
        {
          name: '全渠道已售',
          type: 'bar',
          stack: 'sess',
          data: sold,
          barWidth: '30%',
          itemStyle: { color: '#1565c0' },
        },
        {
          name: '剩余可售',
          type: 'bar',
          stack: 'sess',
          data: remain,
          barWidth: '30%',
          itemStyle: { color: '#90caf9' },
        },
      ],
    };
  }, [sessions]);

  return (
    <Sec>
      <SecTitle>
        分场次销售<em>全部场次 · 累计已售 / 剩余</em>
      </SecTitle>
      <Box>
        <LoadingML loading={loading ? 1 : 0}>
          <Mini>
            <MiniItem>
              <div className="l">总场次</div>
              <div className="v" style={{ color: '#1f2329' }}>
                {sessions.length}
              </div>
            </MiniItem>
            <MiniItem>
              <div className="l">全渠道累计已售</div>
              <div className="v">{fmtThousand(totalSold, 0)}</div>
            </MiniItem>
            <MiniItem>
              <div className="l">剩余可售</div>
              <div className="v">{fmtThousand(totalRemain, 0)}</div>
            </MiniItem>
          </Mini>

          <Plot height="260px">
            <ReactEChartsCore
              echarts={echarts}
              option={option}
              notMerge
              lazyUpdate
              style={{ width: '100%', height: '100%' }}
            />
          </Plot>

          <Note>
            每个场次一根柱，X 轴按开演时间排列（示意 {sessions.length}{' '}
            场，最多可容纳约 80 场）。
            <b>柱高 = 该场次全渠道已售 + 票池剩余可售，不等于全场总座位</b>
            ——他渠预留、内部锁座无法获知，因此不硬拼「总座位」这个分母。
            浅色越高说明该场次越滞销，可直接定位需要加推的场。
            <b>本图为开票以来累计快照</b>
            ，不受上方统计周期筛选影响，因此数量级大于总览的本周期销量。
            <b>仅文广票池剧目为全渠道全量</b>
            ；非文广票池剧目此图仅台上见口径，会低估已售。
          </Note>
        </LoadingML>
      </Box>
    </Sec>
  );
};

export default SessionSales;
